"use client";

import * as React from "react";

import { AUTH_CHANGED_EVENT, hasStoredAuthToken, sessionSnapshot } from "@/app/services/auth-storage";
import api from "@/app/services/api";
import { API_URLS } from "@/app/services/api-urls";

type Listener = () => void;

const listeners = new Set<Listener>();

let isAuthenticatedSnapshot = false;
let refreshPromise: Promise<void> | null = null;
let windowListenersAttached = false;
let observedSession = 'null';
let cookieTimer: ReturnType<typeof setInterval> | null = null;
let checkFailed = false;

function emitAuthState(nextValue: boolean) {
  if (isAuthenticatedSnapshot === nextValue) {
    return;
  }

  isAuthenticatedSnapshot = nextValue;
  listeners.forEach((listener) => listener());
}

async function syncAuthState() {
  if (typeof window === "undefined") {
    return;
  }

  if (!hasStoredAuthToken()) {
    checkFailed = false;
    emitAuthState(false);
    return;
  }

  if (refreshPromise) {
    await refreshPromise;
    if (hasStoredAuthToken() && !isAuthenticatedSnapshot && !checkFailed) return syncAuthState();
    return;
  }

  refreshPromise = (async () => {
    const expected = sessionSnapshot();
    try {
      const result = await api.get<{ success?: boolean }>(API_URLS.IS_AUTHENTICATED);
      if (sessionSnapshot() !== expected) return;
      if (result.data?.success !== true) throw new Error('Login check rejected.');
      checkFailed = false;
      emitAuthState(hasStoredAuthToken());
    } catch {
      checkFailed = hasStoredAuthToken() && sessionSnapshot() === expected;
      if (sessionSnapshot() === expected || !hasStoredAuthToken()) emitAuthState(false);
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

function handleAuthRefresh() {
  if (observedSession !== sessionSnapshot()) {
    checkFailed = false;
    emitAuthState(false);
  }
  observedSession = sessionSnapshot();
  void syncAuthState();
}

function handleVisibilityChange() {
  if (document.visibilityState !== "visible") {
    return;
  }

  void syncAuthState();
}

function attachWindowListeners() {
  if (typeof window === "undefined" || windowListenersAttached) {
    return;
  }

  windowListenersAttached = true;
  window.addEventListener(AUTH_CHANGED_EVENT, handleAuthRefresh);
  window.addEventListener("storage", handleAuthRefresh);
  window.addEventListener("focus", handleAuthRefresh);
  window.addEventListener("pageshow", handleAuthRefresh);
  document.addEventListener("visibilitychange", handleVisibilityChange);
  observedSession = sessionSnapshot();
  cookieTimer = setInterval(() => {
    if (document.visibilityState === 'visible' && sessionSnapshot() !== observedSession) handleAuthRefresh();
  }, 1000);
}

function detachWindowListeners() {
  if (typeof window === "undefined" || !windowListenersAttached) {
    return;
  }

  windowListenersAttached = false;
  window.removeEventListener(AUTH_CHANGED_EVENT, handleAuthRefresh);
  window.removeEventListener("storage", handleAuthRefresh);
  window.removeEventListener("focus", handleAuthRefresh);
  window.removeEventListener("pageshow", handleAuthRefresh);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  if (cookieTimer) clearInterval(cookieTimer);
  cookieTimer = null;
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  attachWindowListeners();

  if (listeners.size === 1) {
    void syncAuthState();
  }

  return () => {
    listeners.delete(listener);

    if (listeners.size === 0) {
      detachWindowListeners();
    }
  };
}

function getSnapshot() {
  return isAuthenticatedSnapshot;
}

export async function checkAuthenticatedUser() {
  await syncAuthState();
  if (checkFailed) throw new Error('Unable to check your login. Please try again.');
  return hasStoredAuthToken() && isAuthenticatedSnapshot;
}

export function useAuthenticatedUser() {
  return React.useSyncExternalStore(subscribe, getSnapshot, () => false);
}
